import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { Job, JobInstance, JobParameter, InstanceParameter } from '../../../models/job';
import { ToastService } from '../../../core/services/toast.service';
import { JobService } from '../../../core/services/api/job.service';
import { Subscription } from 'rxjs';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

interface ResultEntryView {
  key: string;
  displayKey: string;
  value: string;
}

@Component({
  selector: 'app-run-job-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    CheckboxModule,
    MessageModule,
    TooltipModule,
    TranslocoPipe,
  ],
  templateUrl: './job-run-dialog.component.html',
  styleUrls: ['./job-run-dialog.component.scss'],
})
export class RunJobDialogComponent implements OnInit, OnDestroy {
  job: Job | null = null;
  instance: JobInstance | null = null;
  runForm!: FormGroup;
  resultForm: FormGroup | null = null;
  isLoading: boolean = false;
  isRunning: boolean = false;
  isSavingResult: boolean = false;
  hasProcessedBankCheck: boolean = false;
  hasConfirmedSave: boolean = false;
  isResultEditing: boolean = false;
  uploadedFiles: { [key: string]: { name: string; size: number; preview?: string } } = {};
  successMessage: string | null = null;
  resultText: string | null = null;
  resultEntries: ResultEntryView[] = [];
  bankCheckResult: any = null;
  resultImagePreview: string | null = null;
  private lastUploadedImagePreview: string | null = null;
  private resultFormSubscription: Subscription | null = null;

  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private jobService = inject(JobService);
  private cdr = inject(ChangeDetectorRef);
  private translocoService = inject(TranslocoService);

  ngOnInit(): void {
    this.job = this.config.data?.job || null;
    this.instance = this.config.data?.instance || null;

    if (!this.job) {
      this.toastService.error(
        this.getToastTitle('error'),
        this.translate('jobRunDialog.notifications.jobNotFound'),
      );
      this.ref.close();
      return;
    }

    if (!this.instance) {
      this.toastService.error(
        this.getToastTitle('error'),
        this.translate('jobRunDialog.notifications.instanceNotFound'),
      );
      this.ref.close();
      return;
    }

    this.initializeForm();
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translocoService.translate(key, params);
  }

  private getToastTitle(type: 'success' | 'error' | 'info'): string {
    return this.translate(`common.toast.${type}`);
  }

  ngOnDestroy(): void {
    if (this.resultFormSubscription) {
      this.resultFormSubscription.unsubscribe();
      this.resultFormSubscription = null;
    }

    // Clean up all object URLs to prevent memory leaks
    Object.values(this.uploadedFiles).forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
  }

  initializeForm(): void {
    // Get variable parameters as schema
    const variableParameters = this.job?.variableParameters || [];
    const variableParametersControls: { [key: string]: any } = {};

    // Build form controls for each variable parameter
    variableParameters.forEach((param) => {
      const validators = [];
      if (param.required) {
        validators.push(Validators.required);
      }

      // Get default value if available
      let defaultValue = param.default_value;
      if (defaultValue === null || defaultValue === undefined) {
        defaultValue = '';
      }

      // Handle checkbox default value - ensure it's boolean
      if (param.type === 'checkbox') {
        defaultValue = param.default_value !== undefined ? Boolean(param.default_value) : false;
      }

      variableParametersControls[param.variable] = [defaultValue, validators];
    });

    // Create the form with all controls at once
    this.runForm = this.fb.group({
      variableParameters: this.fb.group(variableParametersControls),
    });

    this.cdr.detectChanges();
  }

  private resetResultState(): void {
    this.successMessage = null;
    this.resultText = null;
    this.resultEntries = [];
    this.resultImagePreview = null;
    this.bankCheckResult = null;
    this.hasProcessedBankCheck = false;
    this.hasConfirmedSave = false;
    this.isResultEditing = false;
    this.isSavingResult = false;
    if (this.resultFormSubscription) {
      this.resultFormSubscription.unsubscribe();
      this.resultFormSubscription = null;
    }
    this.resultForm = null;
  }

  private setupResultForm(result: any): void {
    if (this.resultFormSubscription) {
      this.resultFormSubscription.unsubscribe();
      this.resultFormSubscription = null;
    }

    this.resultForm = null;
    this.resultEntries = [];
    this.resultText = null;

    if (this.isPlainObject(result)) {
      const formControls: { [key: string]: any } = {};
      const initialValues = this.mapResultToFormValues(result);
      Object.entries(initialValues).forEach(([key, value]) => {
        formControls[key] = [value];
      });

      this.resultForm = this.fb.group(formControls);
      this.resultEntries = this.buildResultEntriesFromForm();
      this.resultForm.disable({ emitEvent: false });
      this.resultFormSubscription = this.resultForm.valueChanges.subscribe(() => {
        this.resultEntries = this.buildResultEntriesFromForm();
        this.cdr.markForCheck();
      });
    } else {
      const formatted = this.formatResult(result);
      if (Array.isArray(formatted)) {
        this.resultEntries = formatted.map((entry) => ({
          key: entry.key,
          displayKey: entry.key,
          value: entry.value,
        }));
        this.resultText = null;
      } else {
        this.resultText = formatted;
        this.resultEntries = [];
      }
    }
  }

  private isPlainObject(value: any): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private mapResultToFormValues(result: any): Record<string, string> {
    if (!this.isPlainObject(result)) {
      return {};
    }

    const values: Record<string, string> = {};
    Object.entries(result).forEach(([key, value]) => {
      values[key] = this.stringifyValueForForm(value);
    });
    return values;
  }

  private stringifyValueForForm(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }

    return String(value);
  }

  private buildResultEntriesFromForm(): ResultEntryView[] {
    if (!this.resultForm) {
      return [];
    }

    const rawValue = this.resultForm.getRawValue();
    return Object.entries(rawValue).map(([key, value]) => ({
      key,
      displayKey: this.formatKey(key),
      value: this.formatValue(value),
    }));
  }

  private parseFormValue(value: any, originalValue: any): any {
    if (originalValue === null || originalValue === undefined) {
      return value;
    }

    if (typeof originalValue === 'number') {
      const parsedNumber = Number(value);
      return Number.isNaN(parsedNumber) ? value : parsedNumber;
    }

    if (typeof originalValue === 'boolean') {
      if (typeof value === 'boolean') {
        return value;
      }

      const normalized = String(value).trim().toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(normalized)) {
        return true;
      }
      if (['false', '0', 'no', 'n'].includes(normalized)) {
        return false;
      }
      return value;
    }

    if (typeof originalValue === 'object') {
      if (typeof value === 'string' && value.trim() !== '') {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }
      return value;
    }

    return value;
  }

  private buildPayloadFromForm(): any {
    if (this.resultForm) {
      const rawValues = this.resultForm.getRawValue();

      if (this.isPlainObject(this.bankCheckResult)) {
        const payload = { ...this.bankCheckResult };
        Object.entries(rawValues).forEach(([key, value]) => {
          payload[key] = this.parseFormValue(value, this.bankCheckResult[key]);
        });
        return payload;
      }

      return rawValues;
    }

    return this.bankCheckResult;
  }

  getVariableParameters(): JobParameter[] {
    return this.job?.variableParameters || [];
  }

  getInstanceParameters(): InstanceParameter[] {
    return this.instance?.parameters || [];
  }

  getInstanceParameterValue(parameterUuid: string): any {
    const instanceParam = this.instance?.parameters?.find(
      (ip) => ip.parameterUuid === parameterUuid
    );
    return instanceParam?.value || null;
  }

  getVariableParameterFormControl(parameter: JobParameter) {
    return this.runForm.get(`variableParameters.${parameter.variable}`);
  }

  getInstanceName(): string {
    return this.instance?.name || '';
  }

  onRun(): void {
    if (this.runForm.invalid) {
      this.markFormGroupTouched(this.runForm);
      this.toastService.error(
        this.getToastTitle('error'),
        this.translate('jobRunDialog.validation.fillRequired'),
      );
      return;
    }

    this.isRunning = true;
    this.resetResultState();
    const formValue = this.runForm.value;

    // Build parameters object combining instance parameters and variable parameters
    const allParameters: any = {};

    // Add instance parameters (fixed parameters)
    if (this.instance?.parameters) {
      this.instance.parameters.forEach((instanceParam) => {
        const jobParam = this.job?.parameters?.find(p => p.uuid === instanceParam.parameterUuid);
        if (jobParam) {
          allParameters[jobParam.variable] = instanceParam.value;
        }
      });
    }

    // Add variable parameters from form
    const variableParameters = this.getVariableParameters();
    let base64Image: string | null = null;

    variableParameters.forEach((param) => {
      const value = formValue.variableParameters[param.variable];
      if (value !== null && value !== undefined && value !== '') {
        allParameters[param.variable] = value;
        // Extract base64_image if present
        if (param.variable === 'base64_image') {
          base64Image = value;
        }
      }
    });

    // TEMPORARY: Call processBankCheck API if base64_image is available
    if (base64Image) {
      this.jobService.processBankCheck(base64Image)
        .then((result) => {
          this.isRunning = false;
          this.bankCheckResult = result;
          this.hasProcessedBankCheck = true;
          this.hasConfirmedSave = false;
          this.successMessage = this.translate('jobRunDialog.notifications.bankCheckReady');
          this.setupResultForm(result);
          this.resultImagePreview = this.lastUploadedImagePreview;
          this.toastService.success(
            this.getToastTitle('success'),
            this.translate('jobRunDialog.notifications.bankCheckProcessed'),
          );
          this.cdr.detectChanges();
        })
        .catch((error) => {
          this.isRunning = false;
          const errorMessage = error?.message || this.translate('jobRunDialog.notifications.unknownError');
          this.toastService.error(
            this.getToastTitle('error'),
            this.translate('jobRunDialog.notifications.bankCheckFailed', {
              error: errorMessage,
            }),
          );
          this.cdr.detectChanges();
        });
    } else {
      // Fallback to regular job execution if no base64_image
      if (this.job?.uuid) {
        this.jobService.executeJob(this.job.uuid, allParameters)
          .then((result) => {
            this.isRunning = false;
            this.toastService.success(
              this.getToastTitle('success'),
              this.translate('jobRunDialog.notifications.jobExecuted'),
            );
            this.cdr.detectChanges();
            this.ref.close(result);
          })
          .catch((error) => {
            this.isRunning = false;
            const errorMessage = error?.message || this.translate('jobRunDialog.notifications.unknownError');
            this.toastService.error(
              this.getToastTitle('error'),
              this.translate('jobRunDialog.notifications.jobExecutionFailed', {
                error: errorMessage,
              }),
            );
            this.cdr.detectChanges();
          });
      } else {
        this.isRunning = false;
        this.toastService.error(
          this.getToastTitle('error'),
          this.translate('jobRunDialog.notifications.jobUuidMissing'),
        );
        this.cdr.detectChanges();
      }
    }
  }

  onEditResult(): void {
    if (!this.resultForm) {
      return;
    }

    this.isResultEditing = true;
    this.resultForm.enable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  onCancelResultEdit(): void {
    if (!this.resultForm) {
      return;
    }

    const resetValues = this.mapResultToFormValues(this.bankCheckResult);
    this.resultForm.reset(resetValues);
    this.resultForm.disable({ emitEvent: false });
    this.isResultEditing = false;
    this.resultEntries = this.buildResultEntriesFromForm();
    this.cdr.detectChanges();
  }

  onConfirmSave(): void {
    if (this.isSavingResult) {
      return;
    }

    const payload = this.buildPayloadFromForm();
    const wasEditing = this.isResultEditing;
    if (wasEditing && this.resultForm) {
      this.resultForm.disable({ emitEvent: false });
      this.isResultEditing = false;
    }
    this.isSavingResult = true;

    this.jobService.saveBankCheck(payload)
      .then((response) => {
        this.isSavingResult = false;
        const hasResponsePayload = response && typeof response === 'object' && !Array.isArray(response) && Object.keys(response).length > 0;
        this.bankCheckResult = hasResponsePayload ? response : payload;
        this.hasProcessedBankCheck = true;
        this.hasConfirmedSave = true;
        this.isResultEditing = false;
        const successMessage = this.translate('jobRunDialog.notifications.bankCheckSaved');
        this.toastService.success(this.getToastTitle('success'), successMessage);
        this.setSuccessState(successMessage, this.bankCheckResult);
        this.cdr.detectChanges();
      })
      .catch((error) => {
        this.isSavingResult = false;
        if (wasEditing && this.resultForm) {
          this.resultForm.enable({ emitEvent: false });
          this.isResultEditing = true;
          this.resultEntries = this.buildResultEntriesFromForm();
        }
        const errorMessage = error?.message || this.translate('jobRunDialog.notifications.unknownError');
        this.toastService.error(
          this.getToastTitle('error'),
          this.translate('jobRunDialog.notifications.bankCheckSaveFailed', {
            error: errorMessage,
          }),
        );
        this.cdr.detectChanges();
      });
  }

  private setSuccessState(message: string, result: any): void {
    this.successMessage = message;
    this.bankCheckResult = result;
    this.setupResultForm(result);
    this.resultImagePreview = this.lastUploadedImagePreview;
  }

  private formatResult(result: any): { key: string; value: string }[] | string {
    if (result === null || result === undefined) {
      return '';
    }

    if (typeof result === 'string') {
      return result;
    }

    if (typeof result === 'object' && !Array.isArray(result)) {
      return Object.entries(result).map(([key, value]) => ({
        key: this.formatKey(key),
        value: this.formatValue(value),
      }));
    }

    try {
      return JSON.stringify(result, null, 2);
    } catch (error) {
      return String(result);
    }
  }

  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'boolean') {
      return this.translate(value ? 'common.state.yes' : 'common.state.no');
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    if (Array.isArray(value)) {
      return value.length
        ? value.map((item) => this.formatValue(item)).join(', ')
        : '';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }

    return String(value);
  }

  onCancel(): void {
    this.ref.close();
  }

  onFileSelected(event: Event, param: JobParameter): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    if (!file) {
      return;
    }
    this.handleFileSelection(file, param, input);
  }

  onFileDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onFileDrop(event: DragEvent, param: JobParameter, inputElement: HTMLInputElement): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    this.handleFileSelection(file, param, inputElement);
  }

  private handleFileSelection(file: File, param: JobParameter, inputElement?: HTMLInputElement): void {
    // Validate file type (images only)
    if (!file.type.startsWith('image/')) {
      this.toastService.error(
        this.getToastTitle('error'),
        this.translate('jobRunDialog.notifications.invalidImageType'),
      );
      if (inputElement) {
        inputElement.value = '';
      }
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      this.toastService.error(
        this.getToastTitle('error'),
        this.translate('jobRunDialog.notifications.fileSizeExceeded'),
      );
      if (inputElement) {
        inputElement.value = '';
      }
      return;
    }

    const existingPreview = this.uploadedFiles[param.variable]?.preview;
    if (existingPreview) {
      URL.revokeObjectURL(existingPreview);
    }

    // Create preview URL for image files
    const previewUrl = URL.createObjectURL(file);

    // Store file info for display
    this.uploadedFiles[param.variable] = {
      name: file.name,
      size: file.size,
      preview: previewUrl,
    };
    this.lastUploadedImagePreview = previewUrl;

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove data:image/...;base64, prefix if present
      const base64Data = base64String.includes(',')
        ? base64String.split(',')[1]
        : base64String;

      // Set the form control value
      const control = this.getVariableParameterFormControl(param);
      if (control) {
        control.setValue(base64Data);
        control.markAsTouched();
        this.cdr.detectChanges();
      }
    };
    reader.onerror = () => {
      this.toastService.error(
        this.getToastTitle('error'),
        this.translate('jobRunDialog.notifications.fileReadFailed'),
      );
      // Clean up preview URL if it was created
      const storedPreview = this.uploadedFiles[param.variable]?.preview;
      if (storedPreview) {
        URL.revokeObjectURL(storedPreview);
      }
      delete this.uploadedFiles[param.variable];
      if (storedPreview && this.lastUploadedImagePreview === storedPreview) {
        this.lastUploadedImagePreview = null;
      }
      if (storedPreview && this.resultImagePreview === storedPreview) {
        this.resultImagePreview = null;
      }
      if (inputElement) {
        inputElement.value = '';
      }
    };
    reader.readAsDataURL(file);
  }

  getUploadedFileName(param: JobParameter): string | null {
    return this.uploadedFiles[param.variable]?.name || null;
  }

  getUploadedFileSize(param: JobParameter): number | null {
    return this.uploadedFiles[param.variable]?.size || null;
  }

  getFileSizeDisplay(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  removeFile(param: JobParameter, inputElement: HTMLInputElement): void {
    const control = this.getVariableParameterFormControl(param);
    if (control) {
      control.setValue('');
      control.markAsUntouched();
    }
    // Clean up preview URL to prevent memory leaks
    const storedPreview = this.uploadedFiles[param.variable]?.preview;
    if (storedPreview) {
      URL.revokeObjectURL(storedPreview);
    }
    delete this.uploadedFiles[param.variable];
    if (storedPreview && this.lastUploadedImagePreview === storedPreview) {
      this.lastUploadedImagePreview = null;
    }
    if (storedPreview && this.resultImagePreview === storedPreview) {
      this.resultImagePreview = null;
    }
    inputElement.value = '';
    this.cdr.detectChanges();
  }

  getImagePreview(param: JobParameter): string | null {
    return this.uploadedFiles[param.variable]?.preview || null;
  }

  isImageFile(fileName: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const lowerFileName = fileName.toLowerCase();
    return imageExtensions.some(ext => lowerFileName.endsWith(ext));
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((ctrl) => {
          if (ctrl instanceof FormGroup) {
            this.markFormGroupTouched(ctrl);
          } else {
            ctrl.markAsTouched();
          }
        });
      }
    });
  }
}
